import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useLocation } from 'react-router-dom';
import qs from 'qs';

import { Loader } from 'ui';
import { esgAvailable as impactAvailable } from 'services/esg';
import { useAccess, useQuery } from 'shared/hooks';
import { findModule } from 'shared/functions';
import { getEntityAction } from 'store/entity/entityActions';
import { lastVisitedRatingTypeAction } from 'store/shared/lastVisited/lastVisitedActions';
import { moduleTypes } from 'shared/constants/moduleTypes';
import { companyEntityTabs, getCurrentEnvironmentModules } from 'shared/constants/company';
import { EnvironmentTypes } from 'shared/constants/environmentTypes';
import { getEntity as getEntitySelector } from 'selectors/entity';
import { useFetchPortfolioItemsByArtifactId } from 'modules/common/GraphQL/portfolios/hooks';
import { EntityContent } from './EntityContent';
import { EmptyCustomEntityContent } from './EmptyCustomEntityContent';

const Entity = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { module } = useQuery();
  const isDevelopmentEnvironment = useAccess({
    environment: EnvironmentTypes.DEVELOPMENT,
  });
  const isTestingEnvironment = useAccess({
    environment: EnvironmentTypes.TESTING,
  });
  const currentEnvironmentModules = getCurrentEnvironmentModules(
    isDevelopmentEnvironment || isTestingEnvironment,
  ).filter((item) => !module || item.module === module);

  const { entityId } = useParams();
  const [easyNumber] = useState();
  const [activeTabs, setActiveTabs] = useState({
    companyModule: currentEnvironmentModules[0].value,
    companyTab: companyEntityTabs[0].value,
  });

  const [isEmptyCustomCompany, setIsEmptyCustomCompany] = useState(false);
  const [portfolioEntityData, setPortfolioEntityData] = useState(null);

  const { data, instruments, shouldRefresh, isRatingRequestPending, isPending } =
    useSelector(getEntitySelector);

  const [esgAvailability, setESGAvailability] = useState(undefined);

  const isActiveTab = (moduleType) =>
    currentEnvironmentModules.find(findModule(moduleType)) &&
    moduleType === activeTabs.companyModule;

  const getEntity = () => dispatch(getEntityAction({ id: entityId }));

  // FIXME: this part should be done only as a fallback in case we don't get any data from modules
  const {
    data: entityData,
    loading: portfolioLoading,
    refetch: portfolioRefresh,
  } = useFetchPortfolioItemsByArtifactId(entityId, 0, 1);

  const refreshEntity = () => {
    getEntity();
    portfolioRefresh();
  };

  const esgIsPending = isActiveTab(moduleTypes.ESG_IMPACT) && esgAvailability === undefined;

  const getESGAvailability = async () => {
    const response = await impactAvailable(entityId);
    setESGAvailability(response);
  };

  useEffect(() => {
    if (entityId) {
      getEntity();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  useEffect(() => {
    if (shouldRefresh) {
      getEntity();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRefresh]);

  useEffect(() => {
    const { companyTab, companyModule } = qs.parse(location.search, { ignoreQueryPrefix: true });

    if (companyEntityTabs.map(({ value }) => value).includes(companyTab)) {
      setActiveTabs((currentValues) => ({ ...currentValues, companyTab }));
    }

    if (currentEnvironmentModules.map(({ value }) => value).includes(companyModule)) {
      setActiveTabs((currentValues) => ({ ...currentValues, companyModule }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeTabs.companyModule === moduleTypes.SUMMARY_CARD) {
      dispatch(lastVisitedRatingTypeAction({ ratingType: moduleTypes.CREDIT_RATING }));
      return;
    }

    dispatch(lastVisitedRatingTypeAction({ ratingType: activeTabs.companyModule }));
  }, [activeTabs.companyModule]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (entityId) {
      if (isActiveTab(moduleTypes.ESG_IMPACT)) {
        getESGAvailability();
      } else {
        setESGAvailability(undefined);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, activeTabs.companyModule, shouldRefresh]);

  useEffect(() => {
    if (!isPending && !portfolioLoading && !data) {
      if (!entityData?.portfolioItems) {
        window.location.href = '/ca/not-found';
      } else {
        setPortfolioEntityData(entityData.portfolioItems[0]);
        setIsEmptyCustomCompany(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPending, data, portfolioLoading, entityData]);

  if (isPending || portfolioLoading || esgIsPending || !(data || portfolioEntityData))
    return <Loader center />;

  return isEmptyCustomCompany ? (
    <EmptyCustomEntityContent
      entityId={entityId}
      entityData={portfolioEntityData}
      refreshEntity={refreshEntity}
    />
  ) : (
    <EntityContent
      easyNumber={easyNumber}
      instruments={instruments}
      isRatingRequestPending={isRatingRequestPending}
      esgAvailability={esgAvailability}
      entityId={entityId}
      activeTabs={activeTabs}
      isActiveTab={isActiveTab}
      data={data}
      setActiveTabs={setActiveTabs}
      currentEnvironmentModules={currentEnvironmentModules}
      isPending={isPending}
    />
  );
};

export { Entity };
